import React, { useState, useEffect, useRef } from 'react'
import { useUser } from 'util/UserContext'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'ui/InputSelect'
import { updatePrivateAccountStatus, updateMessagePrivacy, updateDefaultAudience, updateMessageSelectUsers, MessageReceiveOption } from 'util/userUtils'
import { PrivacyLevel } from 'util/userUtils'
import { Globe, Users, UserCheck, HeartHandshake, Lock, EyeOff, Download, Upload, Key, AlertCircle, Loader2 } from 'lucide-react'
import { exportAllKeys, importKeysFromTxt } from 'util/anonTokenBackup'
import { getActualVaultTokenCount, getAllVaultKeys } from 'util/anonVaultUtils'
import { toast } from 'sonner'
import { Input } from 'ui/Input'
import { useChatRecipientSearchQuery } from 'hooks/queries/useChatQueries'

interface SelectedMessageUser {
    id: number;
    username: string;
    display_name: string;
}

const UserSettingsPrivacy: React.FC = () => {
    const { currentUser, awaitingUser } = useUser()
    const [isPrivate, setIsPrivate] = useState<boolean>(false)
    const [defaultAudience, setDefaultAudience] = useState<PrivacyLevel>(currentUser?.properties_public?.is_private ? 'follow' : 'public')
    const [msgEveryone, setMsgEveryone] = useState<boolean>(true)
    const [msgFollowers, setMsgFollowers] = useState<boolean>(false)
    const [msgParteaMembers, setMsgParteaMembers] = useState<boolean>(false)
    const [msgBesteas, setMsgBesteas] = useState<boolean>(false)
    const [msgSelectUsers, setMsgSelectUsers] = useState<boolean>(false)
    const [msgNoOne, setMsgNoOne] = useState<boolean>(false)
    const [messageSearch, setMessageSearch] = useState<string>("")
    const [selectedMessageUsers, setSelectedMessageUsers] = useState<SelectedMessageUser[]>([])
    
    // Anonymous keys state
    const [keyCount, setKeyCount] = useState<number>(0)
    const [isExporting, setIsExporting] = useState<boolean>(false)
    const [isImporting, setIsImporting] = useState<boolean>(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const recipientSearchQuery = useChatRecipientSearchQuery(messageSearch, msgSelectUsers)

    // Load actual vault key count
    useEffect(() => {
        void (async () => {
            try {
                const count = await getActualVaultTokenCount();
                setKeyCount(count);
            } catch {
                setKeyCount(0);
            }
        })();
    }, []);

    // Initialize settings from currentUser. Auth-unauth redirect is handled
    // by the parent `UserSettings` route via `RequireAuth`, so this effect
    // can assume `currentUser` will eventually be non-null.
    useEffect(() => {
        if (!currentUser) {
            return
        }

        // Initialize private account setting
        const isUserPrivate = currentUser?.properties_public?.is_private === undefined ? false : currentUser?.properties_public?.is_private
        setIsPrivate(isUserPrivate)
        
        // Initialize default audience
        const savedAudience = currentUser?.properties?.privacy_content_default_audience.value ? currentUser?.properties?.privacy_content_default_audience.value : currentUser?.properties_public?.is_private ? 'follow' : 'public'
        setDefaultAudience(savedAudience as PrivacyLevel)

        // Initialize message settings - simple array check
        const messageSettings = currentUser?.properties?.privacy_message_receive?.value ?? ['public']
        setMsgEveryone(messageSettings.includes('public'))
        setMsgFollowers(messageSettings.includes('followers') || messageSettings.includes('follow'))
        setMsgParteaMembers(messageSettings.includes('partea_members') || messageSettings.includes('teams'))
        setMsgBesteas(messageSettings.includes('besteas'))
        setMsgSelectUsers(messageSettings.includes('select_users'))
        setMsgNoOne(messageSettings.includes('no_one'))
        const rawSelectedUserIds = Array.isArray(currentUser?.properties?.privacy_message_select_users?.value)
            ? currentUser.properties.privacy_message_select_users.value
            : []
        setSelectedMessageUsers(
            rawSelectedUserIds.map((userId: number) => ({
                id: Number(userId),
                username: `user_${userId}`,
                display_name: `User #${userId}`
            }))
        )

    }, [currentUser])

    const handlePrivateAccountChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPrivateValue = e.target.checked
        try {
            const response = await updatePrivateAccountStatus(newPrivateValue)
            const updatedPrivateValue = response.data.properties_public?.is_private ?? false
            setIsPrivate(updatedPrivateValue)
            if (currentUser) {
                currentUser.properties_public = {
                    ...currentUser.properties_public,
                    is_private: updatedPrivateValue
                }
            }
        } catch (error) {
            console.error('Error updating privacy settings:', error)
        }
    }

    const persistMessagePrivacy = async (options: MessageReceiveOption[]) => {
        await updateMessagePrivacy(options)
        if (currentUser) {
            currentUser.properties.privacy_message_receive.value = options
        }
    }

    const handleMessagePrivacyChange = async (
        type: "public" | "followers" | "partea_members" | "besteas" | "select_users" | "no_one",
        checked: boolean
    ) => {
        try {
            const nextNoOne = type === "no_one" ? checked : msgNoOne
            const nextEveryone = type === "public" ? checked : (type === "no_one" && checked ? false : msgEveryone)
            const nextFollowers = type === "followers" ? checked : (type === "no_one" && checked ? false : msgFollowers)
            const nextParteaMembers = type === "partea_members" ? checked : (type === "no_one" && checked ? false : msgParteaMembers)
            const nextBesteas = type === "besteas" ? checked : (type === "no_one" && checked ? false : msgBesteas)
            const nextSelectUsers = type === "select_users" ? checked : (type === "no_one" && checked ? false : msgSelectUsers)

            setMsgNoOne(nextNoOne)
            setMsgEveryone(nextEveryone)
            setMsgFollowers(nextFollowers)
            setMsgParteaMembers(nextParteaMembers)
            setMsgBesteas(nextBesteas)
            setMsgSelectUsers(nextSelectUsers)

            const newValues: MessageReceiveOption[] = []
            if (nextNoOne) {
                newValues.push("no_one")
            } else {
                if (nextEveryone) newValues.push("public")
                if (nextFollowers) newValues.push("followers")
                if (nextParteaMembers) newValues.push("partea_members")
                if (nextBesteas) newValues.push("besteas")
                if (nextSelectUsers) newValues.push("select_users")
            }
            await persistMessagePrivacy(newValues)
        } catch (error) {
            console.error('Error updating message privacy:', error)
        }
    }

    const persistSelectedUsers = async (nextUsers: SelectedMessageUser[]) => {
        const nextIds = nextUsers.map((user) => user.id)
        await updateMessageSelectUsers(nextIds)
        if (currentUser) {
            currentUser.properties.privacy_message_select_users = { value: nextIds }
        }
        setSelectedMessageUsers(nextUsers)
    }

    const handleDefaultAudienceChange = async (value: PrivacyLevel) => {
        try {
            await updateDefaultAudience(value)
            setDefaultAudience(value)
            if (currentUser) {
                currentUser.properties.privacy_content_default_audience.value = value
            }
        } catch (error) {
            console.error('Error updating default audience:', error)
        }
    }

    // Define privacy options with icons for visual representation
    const privacyOptions = [
        { value: 'me', label: 'Just me', icon: Lock },
        { value: 'besteas', label: 'Besteas', icon: HeartHandshake },
        { value: 'follow', label: 'Followers', icon: UserCheck },
        { value: 'teams', label: 'Parteas', icon: Users },
        { value: 'public', label: 'Public', icon: Globe },
        { value: 'anonymous', label: 'Anonymous', icon: EyeOff }
    ]

    if (!currentUser || awaitingUser) {
        return (
            <LoadingSkeleton variant="settings" count={3} className=""/>
        )
    }

    return (
        <div className="p-2">
            <div className="space-y-3">
                {/* Interactions Section */}
                <div className="space-y-2">
                    <label className="form-label text-base font-medium">Interactions</label>
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Private account</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">
                                When enabled, approval is required to follow you.
                            </p>
                        </div>
                        <input
                            type="checkbox"
                            checked={isPrivate}
                            onChange={handlePrivateAccountChange}
                            className="toggle-switch-input col-span-1 flex-shrink-0"
                        />
                    </div>
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start flex-grow">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Messages</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub mb-4">
                                Choose who can message you.
                            </p>
                            <div className="w-full space-y-2">
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><Globe className="size-4" /> Public</span>
                                    <input
                                        type="checkbox"
                                        checked={msgEveryone}
                                        onChange={(e) => handleMessagePrivacyChange('public', e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0"
                                    />
                                </div>
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><UserCheck className="size-4" /> Followers</span>
                                    <input
                                        type="checkbox"
                                        checked={msgFollowers}
                                        onChange={(e) => handleMessagePrivacyChange('followers', e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0"
                                    />
                                </div>
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><Users className="size-4" /> Parteas you share</span>
                                    <input
                                        type="checkbox"
                                        checked={msgParteaMembers}
                                        onChange={(e) => handleMessagePrivacyChange('partea_members', e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0"
                                    />
                                </div>
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><HeartHandshake className="size-4" /> Besteas</span>
                                    <input
                                        type="checkbox"
                                        checked={msgBesteas}
                                        onChange={(e) => handleMessagePrivacyChange('besteas', e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0"
                                    />
                                </div>
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><Lock className="size-4" /> Selected users</span>
                                    <input
                                        type="checkbox"
                                        checked={msgSelectUsers}
                                        onChange={(e) => handleMessagePrivacyChange('select_users', e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0"
                                    />
                                </div>
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><EyeOff className="size-4" /> No one</span>
                                    <input
                                        type="checkbox"
                                        checked={msgNoOne}
                                        onChange={(e) => handleMessagePrivacyChange('no_one', e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0"
                                    />
                                </div>
                                {msgSelectUsers ? (
                                    <div className="rounded-xl border border-eoe-border-main dark:border-eoe-dark-border-main p-3 space-y-3">
                                        <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                            Allow specific users even if they are not followers, partea-mates, or besteas.
                                        </div>
                                        <Input
                                            value={messageSearch}
                                            onChange={(event) => setMessageSearch(event.target.value)}
                                            placeholder="Search usernames"
                                        />
                                        {selectedMessageUsers.length > 0 ? (
                                            <div className="flex flex-wrap gap-2">
                                                {selectedMessageUsers.map((user) => (
                                                    <button
                                                        key={user.id}
                                                        type="button"
                                                        onClick={() => void persistSelectedUsers(selectedMessageUsers.filter((item) => item.id !== user.id))}
                                                        className="rounded-full border border-eoe-border-main dark:border-eoe-dark-border-main px-3 py-1 text-xs"
                                                    >
                                                        {user.display_name || user.username} ×
                                                    </button>
                                                ))}
                                            </div>
                                        ) : null}
                                        {recipientSearchQuery.data && recipientSearchQuery.data.filter((item) => item.type === "user").length > 0 ? (
                                            <div className="space-y-2">
                                                {recipientSearchQuery.data
                                                    .filter((item) => item.type === "user")
                                                    .slice(0, 6)
                                                    .map((result) => (
                                                        <button
                                                            key={result.id}
                                                            type="button"
                                                            onClick={() => {
                                                                if (selectedMessageUsers.some((user) => user.id === result.id)) {
                                                                    return
                                                                }
                                                                void persistSelectedUsers([
                                                                    ...selectedMessageUsers,
                                                                    {
                                                                        id: result.id,
                                                                        username: result.username || "",
                                                                        display_name: result.display_name
                                                                    }
                                                                ])
                                                                setMessageSearch("")
                                                            }}
                                                            className="flex w-full items-center justify-between rounded-xl bg-transparent px-3 py-2 text-left hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                                                        >
                                                            <span className="text-sm text-eoe-text dark:text-eoe-dark-text">
                                                                {result.display_name || result.username}
                                                            </span>
                                                            <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                                                u/{result.username}
                                                            </span>
                                                        </button>
                                                    ))}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="space-y-2">
                    <label className="form-label text-base font-medium">Content</label>
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start flex-grow">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Default audience</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub mb-4">
                                Choose the default audience when publishing content; you can modify this on a per-content basis.
                            </p>
                            <Select onValueChange={handleDefaultAudienceChange} value={defaultAudience}>
                                <SelectTrigger className="input-select px-3 py-1 rounded-md text-sm">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="border border-eoe-border-main dark:border-eoe-dark-border-main">
                                    {privacyOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            <div className="flex items-center gap-2">
                                                <option.icon className="size-4" />
                                                <span>{option.label}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                {/* Anonymous Keys Section */}
                <div className="space-y-2">
                    <label className="form-label text-base font-medium">Anonymous keys</label>
                    
                    {/* Key Count Display */}
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-eoe-icon dark:bg-eoe-dark-icon rounded-full">
                                <Key className="size-5 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke" />
                            </div>
                            <div className="flex flex-col items-start">
                                <span className="text-eoe-text dark:text-eoe-dark-text text-sm font-medium">{keyCount} {keyCount === 1 ? 'key' : 'keys'} on this device</span>
                                <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    Keys let you edit/delete anonymous posts
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Export Keys */}
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start flex-grow">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Export all keys</span>
                            <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub mb-3">
                                Download a .txt file containing all your anonymous edit keys.
                            </p>
                            <button
                                onClick={async () => {
                                    try {
                                        setIsExporting(true);
                                        const allKeys = await getAllVaultKeys();
                                        if (allKeys.length === 0) {
                                            toast.info('No anonymous keys to export');
                                            return;
                                        }
                                        const exported = await exportAllKeys(allKeys);
                                        toast.success(`Exported ${exported} edit key${exported !== 1 ? 's' : ''}`);
                                    } catch (err) {
                                        console.error('Export error:', err);
                                        toast.error('Failed to export keys');
                                    } finally {
                                        setIsExporting(false);
                                    }
                                }}
                                disabled={isExporting || keyCount === 0}
                                className={`btn-primary text-sm px-4 py-2 rounded-full flex items-center gap-2 ${keyCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <Download className="size-4" />
                                {isExporting ? <Loader2 className="size-4 animate-spin" /> : 'Export keys'}
                            </button>
                        </div>
                    </div>

                    {/* Import Keys */}
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start flex-grow">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Import keys</span>
                            <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub mb-3">
                                Upload a .txt file to restore edit access for anonymous posts.
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".txt,text/plain"
                                onChange={async (event) => {
                                    const file = event.target.files?.[0];
                                    if (!file) return;

                                    try {
                                        setIsImporting(true);
                                        const result = await importKeysFromTxt(file);

                                        if (result.imported > 0) {
                                            const newCount = await getActualVaultTokenCount();
                                            setKeyCount(newCount);
                                            const desc = [];
                                            if (result.skipped > 0) desc.push(`${result.skipped} skipped (older)`);
                                            if (result.errors > 0) desc.push(`${result.errors} failed`);
                                            toast.success(
                                                `Imported ${result.imported} key${result.imported !== 1 ? 's' : ''}`,
                                                {
                                                    description: desc.length > 0 ? desc.join(', ') : undefined
                                                }
                                            );
                                        } else if (result.skipped > 0) {
                                            toast.info(`All ${result.skipped} key${result.skipped !== 1 ? 's' : ''} already up to date`, {
                                                description: 'Existing tokens are newer than imported ones'
                                            });
                                        } else {
                                            toast.error('No valid keys found in file', {
                                                description: result.errors > 0 ? `${result.errors} errors encountered` : undefined
                                            });
                                        }
                                    } catch (err) {
                                        console.error('Import error:', err);
                                        toast.error('Failed to import keys');
                                    } finally {
                                        setIsImporting(false);
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                        }
                                    }
                                }}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isImporting}
                                className="btn-secondary text-sm px-4 py-2 rounded-full flex items-center gap-2"
                            >
                                <Upload className="size-4" />
                                {isImporting ? <Loader2 className="size-4 animate-spin" /> : 'Import keys'}
                            </button>
                        </div>
                    </div>

                    {/* Security Note */}
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="size-4 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-yellow-700 dark:text-yellow-400">
                                <strong>Keep key files secure.</strong> Anyone with your key file can edit or delete those anonymous posts. Keys rotate after each edit - export again to refresh your backup.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UserSettingsPrivacy 